import { useEffect, useMemo, useRef, useState } from "react"

import { App, Flex, Segmented, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router"
import { useSearchParams } from "react-router-dom"

import Icon from "../../Icon"
import { getQuickList } from "../LayoutOnboarding/utils/helpers/getQuickList.js"
import AuthActions from "../LoginPage/reducer.js"
import { useFetchBusinessAccount } from "../User/hooks/useFetchBusinessAccount.js"
import BusinessStepsBlock from "./componets/BusinessStepsBlock"
import OnboardingStepsBlock from "./componets/OnboardingStepsBlock"
import QuickLinkItem from "./componets/QuickLinkItem"

import "./styles.less"

const { Title, Text } = Typography
const { confirmEmailRequest, authErrorReset } = AuthActions

const GettingStarted = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { pathname } = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const code = searchParams?.get("code")
  const emailVerified = searchParams?.get("emailVerified")
  const { notification } = App.useApp()
  useFetchBusinessAccount()

  const fetching = useSelector((state) => state.auth.fetching)
  const error = useSelector((state) => state.auth.error)
  const isMobile = useSelector((state) => state.nav.isMobile)
  const [mobileTab, setMobileTab] = useState("overview")

  const path = pathname.split("/")
  const isNewBusiness = pathname?.includes("/new-business")

  const quickList = useMemo(() => getQuickList(`/${path[1]}`), [path])
  const verificationRequestedRef = useRef(false)

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content" }
  }

  useEffect(() => {
    if (emailVerified !== "true") return

    notification.success({
      message: "Your email has been successfully verified.",
      ...notificationConfig
    })

    searchParams.delete("emailVerified")
    setSearchParams(searchParams, { replace: true })
  }, [emailVerified])

  useEffect(() => {
    if (!code) return

    dispatch(confirmEmailRequest(code, pathname))
    verificationRequestedRef.current = true
  }, [code])

  useEffect(() => {
    if (!error) return
    if (!error?.message?.detail) return

    notification.error({
      message: error?.message?.detail,
      ...notificationConfig
    })
    dispatch(authErrorReset())
  }, [error])

  useEffect(() => {
    if (!verificationRequestedRef.current) return
    if (fetching) return
    if (error) {
      verificationRequestedRef.current = false
      return
    }

    notification.success({
      message: "Your email has been successfully verified.",
      ...notificationConfig
    })

    verificationRequestedRef.current = false
  }, [fetching, error])

  const quickLinksContent = (
    <div className="aside_content">
      {quickList.map((i) => (
        <QuickLinkItem
          key={i.title}
          icon={<Icon type={i.icon} />}
          title={i.title}
          description={i.description}
          url={i.url}
          isRecommend={i.recommended}
        />
      ))}
    </div>
  )

  return (
    <div className="getting_started_page">
      <Flex gap={24} style={{ marginBottom: 0 }} vertical={isMobile}>
        <Flex vertical={true} gap={6} style={{ width: !isMobile ? "60%" : "100%" }}>
          <Title level={3} style={{ textAlign: "left" }}>
            Onboarding
          </Title>
          <Text className="subtitle" type="secondary">
            Set up your Chargeback account in just a few minutes by completing these quick steps.
          </Text>
        </Flex>
        {isMobile ? null : <div style={{ width: "40%" }}></div>}
      </Flex>
      {isMobile ? (
        <>
          <Segmented
            className="getting_started_segmented"
            block
            options={[
              { label: t("Overview"), value: "overview" },
              { label: t("Quick links"), value: "quickLinks" }
            ]}
            value={mobileTab}
            onChange={setMobileTab}
          />
          {mobileTab === "overview" ? (
            <div className="main">
              {isNewBusiness ? <BusinessStepsBlock /> : <OnboardingStepsBlock />}
            </div>
          ) : (
            <div className="aside aside--mobile">{quickLinksContent}</div>
          )}
        </>
      ) : (
        <Flex gap={24}>
          <div className="main">
            {isNewBusiness ? <BusinessStepsBlock /> : <OnboardingStepsBlock />}
          </div>
          <div className="aside">
            Quick links
            {quickLinksContent}
          </div>
        </Flex>
      )}
    </div>
  )
}

export default GettingStarted
